import { computed, makeObservable, observable } from 'mobx'
import { Base } from 'models'
import type { IResponseUser, IResponseUserNumber } from 'entities/Users/api/types'
import { getUserAvatarColor } from 'entities/Users/lib/getUserAvatarColor'
import { EnumUserRoles, type Permissions } from 'entities/Users/model/types'

export class User extends Base {
  id: number
  first_name!: string
  last_name!: string
  full_name!: string
  photo_url!: string
  timezone?: string
  email!: string
  role!: EnumUserRoles
  role_label!: string
  number!: IResponseUserNumber | null
  formatted_number!: string
  item_type!: 'user' // TODO: need backend SWC-3483
  isAway!: boolean
  createdAt = ''
  awayStatusLastDate = '' // TODO: need backend SWC-3483
  hasAccessibleOrganizations = false
  isRequirePftNumber = false
  isRequireNumber = false
  origin!: IResponseUser
  first_promoter_ref_id: IResponseUser['first_promoter_ref_id'] = null
  defaultTeamId: number | null = null
  hasInboxOwnership = false
  permissions: Permissions | null = null
  user_number: string | null = null
  private _use_organization_settings = false

  constructor(item: IResponseUser) {
    super()

    this.id = item.id
    this.syncOrigin(item)

    makeObservable(this, {
      first_name: observable,
      last_name: observable,
      timezone: observable,
      isAway: observable,
      first_promoter_ref_id: observable,
      isAdminOrOwnerRole: computed,
      defaultTeamId: observable,
    })
  }

  syncOrigin = (origin: IResponseUser) => {
    this.id = origin.id
    this.first_name = origin.first_name
    this.last_name = origin.last_name
    this.full_name = origin.full_name
    this.photo_url = origin.photo_url
    this.timezone = origin.timezone || undefined
    this.email = origin.email
    this.role = origin.role
    this.role_label = origin.role_label
    this.number = origin.number || null
    this.formatted_number = origin.formatted_number || ''
    this.item_type = origin.item_type || 'user'
    this.isAway = origin.isAway
    this.origin = origin
    this.first_promoter_ref_id = origin.first_promoter_ref_id
    this.defaultTeamId = origin.default_team_id
    this.createdAt = origin.created_at ?? ''
    this.hasInboxOwnership = origin.is_owned_teams ?? false
    this.permissions = origin.permissions || null
    this.user_number = origin.user_number || null
    this._use_organization_settings = origin.use_organization_settings ?? false
  }

  setAwayStatus = (value: boolean) => {
    this.isAway = value
  }

  updateFirstPromoterRefId = (id: string | null) => {
    this.first_promoter_ref_id = id
  }

  setDefaultTeamId = (id: number) => {
    this.defaultTeamId = id
  }

  get firstPromoterRefId() {
    return this.first_promoter_ref_id
  }

  get hasFirstPromoterRef() {
    return Boolean(this.firstPromoterRefId)
  }

  get useOrganizationSettings() {
    return this._use_organization_settings
  }

  get avatarInfo() {
    return {
      firstName: this.first_name,
      lastName: this.last_name,
      image: this.photo_url,
      color: this.color,
      number: this.number?.formatted_number || this.formatted_number,
    }
  }

  get name() {
    let output = ''

    const { first_name, last_name, full_name, email } = this

    if (first_name) {
      output = first_name

      if (last_name) {
        output += ' ' + last_name
      }
    } else if (full_name) {
      output = full_name
    } else if (email) {
      output = email
    }

    return output
  }

  get color() {
    return getUserAvatarColor(this.id)
  }

  get activeFields() {
    return ['name', 'number', 'email', 'created_at']
  }

  get displayInfo() {
    const name = `${this.first_name || ''} ${this.last_name || ''}`.trim()
    return name || this.number?.formatted_number
  }

  get isOwnerRole() {
    return this.role === EnumUserRoles.OWNER
  }

  get isViewOnlyRole() {
    return this.role === EnumUserRoles.VIEW_ONLY
  }

  get isMemberRole() {
    return this.role === EnumUserRoles.MEMBER
  }

  get isManagerRole() {
    return this.role === EnumUserRoles.MANAGER
  }

  get isAdminRole() {
    return this.role === EnumUserRoles.ADMIN
  }

  get isAdminOrOwnerRole() {
    return this.isAdminRole || this.isOwnerRole
  }
}
